import { createSlice } from "@reduxjs/toolkit";
import {
    fetchEngagePosts,
    createEngagePost,
    deleteEngagePost,
    updateEngagePost,
    addPostReaction,
    addPostComment,
    deletePostComment
} from './thunk';

export const initialState = {
    posts: [],
    loading: false,
    error: null
};

const EngageSlice = createSlice({
    name: 'Engage',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch Posts
        builder.addCase(fetchEngagePosts.pending, (state: any) => {
            state.loading = true;
        });
        builder.addCase(fetchEngagePosts.fulfilled, (state: any, action: any) => {
            state.loading = false;
            if (action.payload?.status) {
                state.posts = action.payload.data;
            }
        });
        builder.addCase(fetchEngagePosts.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload?.error || action.error?.message || 'Failed to fetch engage posts';
        });

        // Create Post
        builder.addCase(createEngagePost.fulfilled, (state: any, action: any) => {
            if (action.payload?.status) {
                state.posts.unshift(action.payload.data);
            }
        });
        builder.addCase(createEngagePost.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || action.error?.message || 'Failed to create engage post';
        });

        // Delete Post
        builder.addCase(deleteEngagePost.fulfilled, (state: any, action: any) => {
            if (action.payload?.status) {
                state.posts = state.posts.filter((post: any) => post.id !== action.payload.data);
            }
        });
        builder.addCase(deleteEngagePost.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || action.error?.message || 'Failed to delete engage post';
        });

        // Update Post
        builder.addCase(updateEngagePost.fulfilled, (state: any, action: any) => {
            if (action.payload?.status) {
                const index = state.posts.findIndex((post: any) => post.id === action.payload.data.id);
                if (index !== -1) {
                    state.posts[index] = action.payload.data;
                }
            }
        });
        builder.addCase(updateEngagePost.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || action.error?.message || 'Failed to update engage post';
        });

        // Add Reaction
        builder.addCase(addPostReaction.fulfilled, (state: any, action: any) => {
            if (action.payload?.status) {
                const postIndex = state.posts.findIndex((post: any) => post.id === action.payload.data.post_id);
                if (postIndex !== -1) {
                    // Check if user already has a reaction on this post
                    const existingReactionIndex = state.posts[postIndex].reactions.findIndex(
                        (reaction: any) => String(reaction.user_id) === String(action.payload.data.user_id)
                    );
                    
                    if (action.payload.data.removed) {
                        // Reaction was removed (toggle behavior)
                        if (existingReactionIndex !== -1) {
                            state.posts[postIndex].reactions.splice(existingReactionIndex, 1);
                        }
                    } else if (action.payload.data.updated) {
                        // Reaction was updated (different type)
                        if (existingReactionIndex !== -1) {
                            state.posts[postIndex].reactions[existingReactionIndex] = action.payload.data;
                        }
                    } else {
                        // New reaction was added
                        if (existingReactionIndex !== -1) {
                            // Remove existing reaction first (shouldn't happen with new backend logic)
                            state.posts[postIndex].reactions.splice(existingReactionIndex, 1);
                        }
                        state.posts[postIndex].reactions.push(action.payload.data);
                    }
                }
            }
        });
        builder.addCase(addPostReaction.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || action.error?.message || 'Failed to add post reaction';
        });

        // Add Comment
        builder.addCase(addPostComment.fulfilled, (state: any, action: any) => {
            if (action.payload?.status) {
                const postIndex = state.posts.findIndex((post: any) => post.id === action.payload.data.post_id);
                if (postIndex !== -1) {
                    state.posts[postIndex].comments.push(action.payload.data);
                }
            }
        });
        builder.addCase(addPostComment.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || action.error?.message || 'Failed to add post comment';
        });

        // Delete Comment
        builder.addCase(deletePostComment.fulfilled, (state: any, action: any) => {
            if (action.payload?.status) {
                state.posts.forEach((post: any) => {
                    post.comments = post.comments.filter((comment: any) => comment.id !== action.payload.data);
                });
            }
        });
        builder.addCase(deletePostComment.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || action.error?.message || 'Failed to delete post comment';
        });


    }
});

export default EngageSlice.reducer;
